import { filter, map, mergeAll, pipe, prop, propEq } from 'ramda';
import { LOG_ACTIONS, logAction } from '@namespace/analytics';
import { getCmsTranslations, getFallbackTranslations } from '../api';

const logTranslationFetchError = (actionName) => (error) => {
  console.error(actionName);
  logAction(actionName, { error });
};

const getMinimalTranslations = (lang) =>
  getCmsTranslations(lang)
    .catch((error) => {
      logTranslationFetchError(LOG_ACTIONS.TRANSLATIONS)(error);

      return getFallbackTranslations(lang);
    })
    .catch((error) => {
      logTranslationFetchError(LOG_ACTIONS.TRANSLATION_FALLBACKS)(error);

      throw error;
    });

const getInitialTranslationsWithFallback = async (lang) => {
  const translationsPromise = getCmsTranslations(lang).catch(
    logTranslationFetchError(LOG_ACTIONS.TRANSLATIONS)
  );

  const fallbackPromise = getFallbackTranslations(lang).catch(
    logTranslationFetchError(LOG_ACTIONS.TRANSLATION_FALLBACKS)
  );

  const result = (
    await Promise.allSettled([fallbackPromise, translationsPromise])
  ).filter(propEq('fulfilled', 'status'));

  if (result.length === 0) {
    throw new Error();
  }

  return pipe(map(prop('value')), mergeAll)(result);
};

const getCmsTranslationsOnly = (lang) =>
  getCmsTranslations(lang).catch(
    logTranslationFetchError(LOG_ACTIONS.TRANSLATIONS)
  );

const getTranslationsWithFallback = async (lang) => {
  const translationsPromise = getCmsTranslations(lang).catch(
    logTranslationFetchError(LOG_ACTIONS.TRANSLATIONS)
  );

  const fallbackPromise = getFallbackTranslations(lang).catch(
    logTranslationFetchError(LOG_ACTIONS.TRANSLATION_FALLBACKS)
  );

  const [translations, fallbacks] = await Promise.allSettled([
    translationsPromise,
    fallbackPromise
  ]);

  if (translations.status === 'rejected') {
    throw new Error();
  }

  return pipe(
    filter(propEq('fulfilled', 'status')),
    map(prop('value')),
    mergeAll
  )([fallbacks, translations]);
};

export const getInitialTranslations = (lang, isLoadFallback) =>
  isLoadFallback
    ? getInitialTranslationsWithFallback(lang)
    : getMinimalTranslations(lang);

export const getTranslations = (lang, isLoadFallback) =>
  isLoadFallback
    ? getTranslationsWithFallback(lang)
    : getCmsTranslationsOnly(lang);
